import React, { useMemo } from 'react';
import { IconCreditCard } from '@tabler/icons-react';

import { useTranslate } from '@src/core/hooks';
import {
  PaymentMethodFormKind,
  resolveMaxInterestFreeInstallments,
  resolvePaymentMethodForm,
  type PaymentOption,
} from '@src/domains/payments';
import { PixIcon } from '@src/icons';

import { PaymentMethodBrands } from './PaymentMethodBrands';
import {
  AccordionBody,
  AccordionIcon,
  AccordionMeta,
  AccordionRoot,
  AccordionTitle,
  AccordionTitleGroup,
  MethodRadio,
  MethodRow,
  MethodSelect,
} from './styles';
import { PaymentMethodChip } from './PaymentMethodChip';

type PaymentMethodAccordionProps = {
  option: PaymentOption;
  selected: boolean;
  onSelect: (option: PaymentOption) => void;
  children?: React.ReactNode;
};

export const PaymentMethodAccordion = (props: PaymentMethodAccordionProps) => {
  const { option, selected, onSelect, children } = props;
  const { i18n } = useTranslate();

  const { id, code, name, discountPercentage } = option.paymentMethod;

  const isPix = resolvePaymentMethodForm(code) === PaymentMethodFormKind.Pix;
  const maxInterestFree = useMemo(() => resolveMaxInterestFreeInstallments(option), [option]);

  const hasBrands = Boolean(option.brands?.length);

  return (
    <AccordionRoot data-testid={`payment-method-${id}`} data-stacked={String(hasBrands)}>
      <MethodRow>
        <MethodSelect>
          <MethodRadio
            value={id}
            checked={selected}
            onChange={() => onSelect(option)}
            slotProps={{ input: { 'aria-expanded': selected } as React.InputHTMLAttributes<HTMLInputElement> }}
          />
          <AccordionTitleGroup>
            <AccordionIcon>{isPix ? <PixIcon aria-hidden /> : <IconCreditCard size={24} aria-hidden />}</AccordionIcon>
            <AccordionTitle>{name}</AccordionTitle>
          </AccordionTitleGroup>
        </MethodSelect>

        <AccordionMeta>
          {discountPercentage ? (
            <PaymentMethodChip
              tone='success'
              label={i18n('checkout_payment_discount_chip', {
                percentage: discountPercentage.toString().replace('.', ','),
              })}
            />
          ) : null}

          {maxInterestFree ? (
            <PaymentMethodChip
              tone='primary'
              label={i18n('checkout_payment_installments_chip', { count: String(maxInterestFree) })}
            />
          ) : null}

          <PaymentMethodBrands brands={option.brands} />
        </AccordionMeta>
      </MethodRow>

      {selected && children ? <AccordionBody>{children}</AccordionBody> : null}
    </AccordionRoot>
  );
};

export default PaymentMethodAccordion;
